/**
 * AI RECOMMENDATIONS DATA
 * BuildRight Construction Ltd
 *
 * All dates are computed dynamically relative to today.
 * These are GENUINE AI insights — pattern detection, cross-data correlation,
 * and predictive reasoning — not simple fact-listing.
 *
 * Insight categories:
 *   OPERATIONAL — payment timing, supplier terms, clustering, early payment discounts
 *   STRATEGIC   — concentration risk, seasonal patterns, anomaly detection, benchmark comparisons
 */

// ── Helpers ──────────────────────────────────────────────────────────────────

const today = new Date();
today.setHours(0, 0, 0, 0);

const daysFromNow = (n: number): string => {
  const d = new Date(today);
  d.setDate(d.getDate() + n);
  return d.toISOString().split("T")[0];
};

const daysAgo = (n: number): string => {
  const d = new Date(today);
  d.setDate(d.getDate() - n);
  return d.toISOString().split("T")[0];
};

// ── Types ─────────────────────────────────────────────────────────────────────

export type RecommendationPriority = "HIGH" | "MEDIUM" | "LOW";
export type RecommendationCategory = "OPERATIONAL" | "STRATEGIC";
export type RecommendationType =
  | "PaymentPattern"
  | "ConcentrationRisk"
  | "SeasonalWarning"
  | "AnomalyDetection"
  | "MilestoneOptimisation"
  | "SupplierTerms"
  | "EarlyPaymentDiscount"
  | "ClusteringRisk";

export interface RecommendationInvoice {
  invoiceNumber: string;
  customerName: string;
  amount: number;
  daysOverdue?: number;
  dueDate?: string;
}

export interface RecommendationBill {
  supplierName: string;
  amount: number;
  currentDueDate: string;
  proposedDueDate: string;
  timingBenefit: number; // days
}

export interface RecurringTransactionSuggestion {
  name: string;
  direction: "inflow" | "outflow";
  amount: number;
  frequency:
    | "Daily"
    | "Weekly"
    | "Fortnightly"
    | "Monthly"
    | "Quarterly"
    | "Annual";
  category: string;
  counterpartyName?: string;
  notes?: string;
}

export interface Recommendation {
  id: string;
  category: RecommendationCategory;
  type: RecommendationType;
  priority: RecommendationPriority;
  title: string;
  summary: string;
  reasoning: string; // The "why AI" — what pattern/data was detected
  impact: number;
  effort: "Low" | "Medium" | "High";
  deadline: string; // human-readable
  suggestedActions: string[];
  invoices?: RecommendationInvoice[];
  bills?: RecommendationBill[];
  analysis?: {
    currentCash: number;
    monthlyRunway: number;
    eligibleInvoices: number;
    availableFinancing: number;
    estimatedCost: number;
    netBenefit: number;
  };
  dismissed?: boolean;
  suggestRecurringTransaction?: RecurringTransactionSuggestion;
  impactHistory?: import("../types/scenarios").ImpactHistoryEntry[];
}

// ── RECOMMENDATION 1 — STRATEGIC: Cash Concentration Risk ────────────────────

const rec1: Recommendation = {
  id: "REC-001",
  category: "STRATEGIC",
  type: "ConcentrationRisk",
  priority: "HIGH",
  title: "Cash Concentration Risk — Over-Reliance on Three Clients",
  summary:
    "61% of expected receipts in the next 60 days are concentrated across just three clients. If any one delays by 30 days, your Day 30 position drops below £80,000.",
  reasoning:
    "Analysis of your receivables pipeline shows that Education Facilities Ltd (£63,554), BigCo Ltd (£22,000), and Local Government Authority (£24,000) account for 61% of all inflows due in the next 60 days. Cross-referencing with your forecast, a 30-day delay from any one of these would reduce your minimum cash from £61,000 to below £45,000 — approaching your operational safety threshold.",
  impact: 109554,
  effort: "Medium",
  deadline: "Review this week",
  suggestedActions: [
    "Confirm milestone sign-off status with Education Facilities Ltd (£63,554 across 3 invoices)",
    "Request payment confirmation from Local Government Authority — their payment history shows 8-day average lateness",
    "Build a contingency buffer plan: identify which outgoings can be deferred if a client delays",
    "Consider requesting payment on account from clients where projects are ongoing",
    "Review contract terms for any milestone-linked payment triggers that can be expedited",
  ],
  invoices: [
    {
      invoiceNumber: "INV-1262",
      customerName: "Education Facilities Ltd",
      amount: 42000,
      dueDate: daysFromNow(12),
    },
    {
      invoiceNumber: "INV-1254",
      customerName: "Education Facilities Ltd",
      amount: 21554,
      dueDate: daysFromNow(14),
    },
    {
      invoiceNumber: "INV-1251",
      customerName: "Local Government Authority",
      amount: 24000,
      dueDate: daysFromNow(18),
    },
    {
      invoiceNumber: "INV-1234",
      customerName: "BigCo Ltd",
      amount: 22000,
      dueDate: daysFromNow(6),
    },
  ],
  impactHistory: [
    {
      id: "ih-rec1-1",
      dateTime: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000),
      refreshType: "Scheduled",
      triggeredBy: "System",
      previousValue: 105000,
      newValue: 109554,
    },
    {
      id: "ih-rec1-2",
      dateTime: new Date(Date.now() - 24 * 60 * 60 * 1000),
      refreshType: "Scheduled",
      triggeredBy: "System",
      previousValue: 109554,
      newValue: 109554,
    },
  ],
};

// ── RECOMMENDATION 2 — STRATEGIC: Seasonal Pattern Warning ───────────────────

const rec2: Recommendation = {
  id: "REC-002",
  category: "STRATEGIC",
  type: "SeasonalWarning",
  priority: "HIGH",
  title: "Q1 Seasonal Cash Dip — Pattern Consistent with Prior Years",
  summary:
    "BuildRight's Q1 receipts typically run 22% below Q4 while costs hold steady. Your current forward commitments of £180,000 hitting January–February may not be fully covered by projected inflows.",
  reasoning:
    "Comparing this year's receivables pipeline against the same 90-day window from the prior two years, Q1 shows a consistent 18–25% reduction in cash inflows. This is typical for construction — fewer project starts, slower client sign-off, and holiday payment delays. Your current Q1 pipeline shows £180,000 in committed outflows against £142,000 in confirmed inflows — a £38,000 gap that would need to be covered by current cash reserves.",
  impact: 38000,
  effort: "Medium",
  deadline: "Plan within 2 weeks",
  suggestedActions: [
    "Bring forward any December invoices that can be issued early to maximise Q4 collections",
    "Identify which Q1 outflows are deferrable without contractual penalty",
    "Consider negotiating January payment terms with two largest suppliers now, while Q4 cash position is strong",
    "Model a conservative Q1 scenario in the Scenarios module to quantify the risk formally",
    "Ensure your overdraft facility is confirmed active and available before year-end",
  ],
  impactHistory: [
    {
      id: "ih-rec2-1",
      dateTime: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000),
      refreshType: "Scheduled",
      triggeredBy: "System",
      previousValue: 34500,
      newValue: 36200,
    },
    {
      id: "ih-rec2-2",
      dateTime: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000),
      refreshType: "Scheduled",
      triggeredBy: "System",
      previousValue: 36200,
      newValue: 38000,
    },
    {
      id: "ih-rec2-3",
      dateTime: new Date(Date.now() - 12 * 60 * 60 * 1000),
      refreshType: "Manual",
      triggeredBy: "Sarah Mitchell",
      previousValue: 38000,
      newValue: 38000,
    },
  ],
};

// ── RECOMMENDATION 3 — OPERATIONAL: Local Government Payment Pattern ──────────

const rec3: Recommendation = {
  id: "REC-003",
  category: "OPERATIONAL",
  type: "PaymentPattern",
  priority: "HIGH",
  title: "Local Government Authority Pays 8 Days Late on Average",
  summary:
    "Based on 11 historical payments, Local Government Authority pays an average of 8.2 days after due date. Their next invoice (£6,900) is due in 3 days — realistically expect it in 11 days, shifting your Day 30 position by £6,900.",
  reasoning:
    "Payment history analysis across 11 invoices from Local Government Authority shows consistent late payment: average 8.2 days, range 5–14 days, zero instances of on-time payment. Their current invoice INV-1235 (£6,900) is due imminently. Forecasting on the contractual due date overstates your Day 7 cash position. Adjusting for actual payment behaviour shifts this receipt to the correct window and avoids a false sense of near-term liquidity.",
  impact: 6900,
  effort: "Low",
  deadline: "Action today",
  suggestedActions: [
    "Contact Local Government Authority accounts payable today to confirm payment is in their next payment run",
    "Adjust your Day 7–14 forecast to reflect realistic 8-day delay rather than contractual due date",
    "For future invoices with this client, add a 10-day buffer to your forecast automatically",
    "Request a payment schedule in writing for current and future invoices",
    'Consider adding a "realistic receipt date" column to your transaction tracking for this client',
  ],
  invoices: [
    {
      invoiceNumber: "INV-1235",
      customerName: "Local Government Authority",
      amount: 6900,
      daysOverdue: 3,
      dueDate: daysFromNow(3),
    },
  ],
};

// ── RECOMMENDATION 4 — OPERATIONAL: Subcontractor Payment Clustering ──────────

const rec4: Recommendation = {
  id: "REC-004",
  category: "OPERATIONAL",
  type: "ClusteringRisk",
  priority: "HIGH",
  title: "Four Subcontractor Payments Clustering in a 3-Day Window",
  summary:
    "£67,422 in subcontractor payments fall within a 3-day window in the next 30 days. This clustering is unusual and will cause a sharp single-day cash dip that your current forecast may not fully show.",
  reasoning:
    "Analysis of your upcoming bill schedule shows an abnormal payment cluster: four subcontractor payments totalling £67,422 are all due within the same 72-hour window. This pattern is atypical — reviewing the payment terms and invoice dates, this appears to have arisen from the Midlands JV mobilisation, where multiple subcontractors were onboarded simultaneously on identical payment terms. Spreading these by 5–7 days each would smooth the cash curve without affecting any contractual obligations.",
  impact: 67422,
  effort: "Low",
  deadline: "Act within 5 days",
  suggestedActions: [
    "Contact each subcontractor and request a 5-7 day payment date adjustment — frame as a mutual cash flow optimisation",
    "Specialist Electrical Subcontractor (£11,622 + £4,900): combine into single payment run, request 7-day shift",
    "Building Materials Supplier (£11,000): request 5-day shift — long-term supplier, low friction",
    "Specialist Carpentry (£6,800): request 5-day shift to avoid same-day clustering",
    "If shifts are agreed, update recurring payment schedule before next run",
    "Flag this as a process improvement: stagger subcontractor payment terms on new project onboarding",
  ],
  bills: [
    {
      supplierName: "Specialist Electrical Subcontractor Ltd",
      amount: 11622,
      currentDueDate: daysFromNow(18),
      proposedDueDate: daysFromNow(25),
      timingBenefit: 7,
    },
    {
      supplierName: "Specialist Electrical Subcontractor Ltd",
      amount: 4900,
      currentDueDate: daysFromNow(18),
      proposedDueDate: daysFromNow(25),
      timingBenefit: 7,
    },
    {
      supplierName: "Building Materials Supplier Ltd",
      amount: 11000,
      currentDueDate: daysFromNow(19),
      proposedDueDate: daysFromNow(24),
      timingBenefit: 5,
    },
    {
      supplierName: "Specialist Carpentry Subcontractor Ltd",
      amount: 6800,
      currentDueDate: daysFromNow(19),
      proposedDueDate: daysFromNow(24),
      timingBenefit: 5,
    },
  ],
  suggestRecurringTransaction: {
    name: "Subcontractor Payment Run — Midlands JV",
    direction: "outflow",
    amount: 67422,
    frequency: "Monthly",
    category: "Subcontractors",
    counterpartyName: "Multiple Subcontractors",
    notes:
      "AI-suggested: Stagger subcontractor payments across a 7-day window to smooth cash curve. Review and adjust individual amounts before activating.",
  },
};

// ── RECOMMENDATION 5 — OPERATIONAL: Milestone Timing Optimisation ─────────────

const rec5: Recommendation = {
  id: "REC-005",
  category: "OPERATIONAL",
  type: "MilestoneOptimisation",
  priority: "MEDIUM",
  title: "Accelerate Midlands JV Milestone Sign-Off to Eliminate Day 30 Dip",
  summary:
    "Three projects hit payment trigger milestones within the next 45 days (£287,000 total). Accelerating sign-off on the Midlands JV by one week brings £95,000 forward and eliminates the projected Day 30 cash dip entirely.",
  reasoning:
    "Cross-referencing your forecast timeline against project milestone schedules, your Day 30 position dips to £61,000 — tight but above threshold. However, the Midlands JV has a payment trigger milestone (practical completion of Phase 1) that your project team confirms is 4–5 days from completion. If sign-off is expedited this week, the associated £95,000 payment moves from Day 38 to Day 28, entirely eliminating the Day 30 dip and adding a £34,000 buffer above minimum cash.",
  impact: 95000,
  effort: "Low",
  deadline: "This week",
  suggestedActions: [
    "Contact Midlands JV project manager to confirm Phase 1 practical completion status",
    "Request sign-off meeting before end of week if works are complete",
    "Issue invoice immediately upon sign-off confirmation — do not wait until standard billing cycle",
    "Ensure client accounts payable contact is briefed to prioritise this payment",
    "If sign-off cannot be expedited, flag Day 30 dip risk to CFO for contingency planning",
  ],
};

// ── RECOMMENDATION 6 — OPERATIONAL: Supplier Terms Benchmark ─────────────────

const rec6: Recommendation = {
  id: "REC-006",
  category: "OPERATIONAL",
  type: "SupplierTerms",
  priority: "MEDIUM",
  title:
    "Plant Hire Payment Terms Below Industry Benchmark — £8,400 Rolling Benefit Available",
  summary:
    "BuildRight is paying Plant & Machinery Rental Ltd on 7-day terms. Construction companies of your size and spend level with this supplier category typically operate on 30-day terms. Renegotiating releases £8,400 on a rolling monthly basis.",
  reasoning:
    "Benchmarking your supplier payment terms against construction industry norms (based on aggregated sector data), Plant & Machinery Rental Ltd is currently on Net 7 terms — significantly below the 28–30 day norm for plant hire at your spend level (£4,347/month). This appears to have originated from your initial contract setup when BuildRight was smaller. You are now in a stronger negotiating position as a regular, high-volume customer. Extending to 30 days frees up £8,400 in working capital on a permanent rolling basis.",
  impact: 8400,
  effort: "Low",
  deadline: "Next supplier review",
  suggestedActions: [
    "Request a payment terms review meeting with Plant & Machinery Rental Ltd account manager",
    "Position as a partnership conversation — you are a valued long-term customer seeking standard industry terms",
    "Propose Net 30 as the target; accept Net 21 as a minimum acceptable outcome",
    "Review all other suppliers on Net 7 terms — there may be similar opportunities elsewhere",
    "Document agreed terms in writing and update payment schedule immediately on agreement",
  ],
  bills: [
    {
      supplierName: "Plant & Machinery Rental Ltd",
      amount: 4347,
      currentDueDate: daysFromNow(7),
      proposedDueDate: daysFromNow(30),
      timingBenefit: 23,
    },
    {
      supplierName: "Plant & Machinery Rental Ltd",
      amount: 4800,
      currentDueDate: daysFromNow(14),
      proposedDueDate: daysFromNow(37),
      timingBenefit: 23,
    },
  ],
  suggestRecurringTransaction: {
    name: "Plant & Machinery Rental — Monthly Hire",
    direction: "outflow",
    amount: 4347,
    frequency: "Monthly",
    category: "Plant & Equipment",
    counterpartyName: "Plant & Machinery Rental Ltd",
    notes:
      "AI-suggested: Monthly plant hire payment. Update amount once Net 30 terms are agreed with supplier.",
  },
};

// ── RECOMMENDATION 7 — STRATEGIC: Utility Cost Anomaly ───────────────────────

const rec7: Recommendation = {
  id: "REC-007",
  category: "STRATEGIC",
  type: "AnomalyDetection",
  priority: "MEDIUM",
  title: "Site B Utility Costs Running 34% Above 6-Month Average",
  summary:
    "Utility costs for Site B have risen 34% above the 6-month rolling average. The same pattern preceded a billing error in April. Worth investigating before the next payment run.",
  reasoning:
    "Variance analysis of your utility cost category shows Site B spending at £3,420/month against a 6-month average of £2,550/month — a sustained 34% elevation over the past 6 weeks. This is not a one-off spike. Reviewing historical data, a similar pattern (28% elevation over 5 weeks) occurred in March–April and was traced to a meter misread that resulted in a double-billing. That error took 6 weeks to resolve and affected cash flow by £4,800. Early detection here creates an opportunity to catch and challenge the same issue before it compounds.",
  impact: 4800,
  effort: "Low",
  deadline: "Before next payment run",
  suggestedActions: [
    "Request meter readings and billing breakdown from utility provider for Site B — last 8 weeks",
    "Cross-reference against Site B project activity levels — has usage genuinely increased?",
    "Compare with Site A utility trends over the same period as a baseline",
    "If a billing error is identified, raise a formal dispute and request a credit note",
    "Flag to site manager to confirm no unusual equipment or extended operating hours",
    "If billing is correct, investigate whether a fixed-rate contract would reduce volatility",
  ],
};

// ── RECOMMENDATION 8 — OPERATIONAL: Early Payment Discount ───────────────────

const rec8: Recommendation = {
  id: "REC-008",
  category: "OPERATIONAL",
  type: "EarlyPaymentDiscount",
  priority: "LOW",
  title:
    "Early Payment Discount on Equipment Hire — Net Positive at Current Cash Level",
  summary:
    "Equipment Hire Company Ltd is offering a 1.5% early payment discount on £12,600 due in 18 days. Based on your current cash position and cost of capital, accepting yields a net £189 benefit. The maths works in your favour right now.",
  reasoning:
    "Equipment Hire Company Ltd is offering 1.5/10 Net 30 terms on invoice BILL-5688 (£12,600). At your current cash level of £247,000 and with your overdraft facility undrawn, your effective cost of capital is low. Paying early (within 10 days) saves £189 — a 1.5% return over 20 days, equivalent to a 27% annualised return on the cash deployed. This is above your typical short-term cash return and represents a low-risk, immediate financial gain. However, this only holds while cash is comfortably above your minimum threshold — this recommendation should not be acted on if cash drops below £150,000.",
  impact: 189,
  effort: "Low",
  deadline: daysFromNow(10),
  suggestedActions: [
    "Confirm current cash position is above £150,000 before acting (current: £247,000 ✓)",
    "Arrange early payment of BILL-5688 (£12,411 net of 1.5% discount) before the 10-day window closes",
    "Notify Equipment Hire Company Ltd accounts team of early payment to ensure discount is applied",
    "Update cash forecast to reflect early payment and the £189 saving",
    "For future invoices, set a calendar reminder to review early payment discount offers against cash position",
  ],
  bills: [
    {
      supplierName: "Equipment Hire Company Ltd",
      amount: 12600,
      currentDueDate: daysFromNow(18),
      proposedDueDate: daysFromNow(8),
      timingBenefit: -10,
    },
  ],
};

// ── All recommendations ───────────────────────────────────────────────────────

export const allRecommendations: Recommendation[] = [
  rec1,
  rec2,
  rec3,
  rec4,
  rec5,
  rec6,
  rec7,
  rec8,
];

// ── Summary metrics ───────────────────────────────────────────────────────────

export const aiSummaryMetrics = {
  totalPotentialImpact: allRecommendations.reduce(
    (sum, r) => sum + r.impact,
    0,
  ),
  fromRecommendations: allRecommendations.length,
  highPriorityActions: allRecommendations.filter((r) => r.priority === "HIGH")
    .length,
  completedToday: 0,
  getStartedMessage: "Get started!",
  avgTimeToComplete: 6,
};

// ── Alert summary (cash trend) ────────────────────────────────────────────────

export const aiAlertSummary = {
  severity: "moderate" as const,
  startingCash: 247000,
  projectedCash90Days: 69000,
  decline: 178000,
  message:
    "Cash is forecast to decline from £247k to £69k at Day 90 — manageable but requiring active management. The insights below highlight where targeted action has the highest impact.",
};

// ── Impact visualisation ──────────────────────────────────────────────────────

export const aiImpactVisualization = {
  before: [
    { date: daysFromNow(0), cash: 247000, label: "Today" },
    { date: daysFromNow(30), cash: 185000, label: "30 Days" },
    { date: daysFromNow(60), cash: 128000, label: "60 Days" },
    { date: daysFromNow(90), cash: 69000, label: "90 Days" },
  ],
  after: [
    { date: daysFromNow(0), cash: 247000, label: "Today" },
    { date: daysFromNow(30), cash: 245000, label: "30 Days (+£60k)" },
    { date: daysFromNow(60), cash: 198000, label: "60 Days (+£70k)" },
    { date: daysFromNow(90), cash: 155000, label: "90 Days (+£86k)" },
  ],
  improvement: 86000,
  concernThreshold: 100000,
  annotations: [
    {
      date: daysFromNow(30),
      note: "Cluster rescheduled, milestone expedited, LGA payment confirmed",
    },
    {
      date: daysFromNow(60),
      note: "Supplier terms extended, concentration risk managed",
    },
  ],
};

// ── Legacy default export (for backward compat with ImpactModal) ──────────────

export const aiRecommendationsData = {
  generatedAt: new Date().toISOString(),
  forUser: "Sarah Mitchell",
  forCompany: "BuildRight Construction Ltd",
  alertSummary: aiAlertSummary,
  recommendations: allRecommendations,
  impactVisualization: aiImpactVisualization,
  summaryMetrics: aiSummaryMetrics,
};
